import {
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ComponentType,
  type ReactNode,
  type MouseEvent,
} from "react";
import { RouterCtx } from "./router-context";

export type Route = {
  path: string;
  Page?: ComponentType;
  redirect?: string;
};

interface RouteEntry extends Route {
  segments: string[];
}

const toEntry = (route: Route): RouteEntry => ({
  ...route,
  segments: route.path.split("/").filter(Boolean),
});

function match(
  segments: string[],
  pathname: string,
): Record<string, string> | null {
  const pathSegments = pathname.split("/").filter(Boolean);
  if (segments.length !== pathSegments.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment.startsWith(":")) {
      params[segment.slice(1)] = pathSegments[i];
    } else if (segment !== pathSegments[i]) {
      return null;
    }
  }
  return params;
}

interface Resolved {
  Page: ComponentType;
  params: Record<string, string>;
}

function resolve(pathname: string, entries: RouteEntry[]): Resolved | null {
  for (let hops = 0; hops < entries.length; hops++) {
    for (const entry of entries) {
      if (entry.segments[0] === "*") return { Page: entry.Page!, params: {} };
      const params = match(entry.segments, pathname);
      if (!params) continue;
      if (entry.redirect) {
        pathname = entry.redirect;
        break;
      }
      return { Page: entry.Page!, params };
    }
  }
  return null;
}

export function RouterProvider({
  routes,
  children,
}: {
  routes: Route[];
  /** Rendered after the page on every route, e.g. the feedback widget. */
  children?: ReactNode;
}) {
  const entries = useMemo(() => routes.map(toEntry), [routes]);
  const [url, setUrl] = useState(window.location.pathname);

  const navigate = (to: string) => {
    if (to === url) return;
    window.history.pushState({}, "", to);
    setUrl(to);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const onPop = () => setUrl(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const route = resolve(url, entries);

  useEffect(() => {
    if (url !== window.location.pathname)
      window.history.replaceState({}, "", url);
  }, [url]);

  if (!route) return null;

  return (
    <RouterCtx.Provider
      value={{ navigate, params: route.params, pathname: url }}
    >
      <route.Page />
      {children}
    </RouterCtx.Provider>
  );
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
}

export function Link({ to, onClick, ...rest }: LinkProps) {
  const { navigate } = useContext(RouterCtx);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  };

  return <a href={to} onClick={handleClick} {...rest} />;
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest;

  const testRoutes: Route[] = [
    { path: "/", Page: () => null },
    { path: "/daily", Page: () => null },
    { path: "/daily/:lobbyId", redirect: "/daily" },
    { path: "/openlobby/:lobbyId/game", Page: () => null },
    { path: "/openlobby/:lobbyId/score", Page: () => null },
    { path: "*", Page: () => null },
  ];
  const testEntries = testRoutes.map(toEntry);

  describe("router matching", () => {
    it("matches the index route", () => {
      const result = resolve("/", testEntries);
      expect(result?.Page).toBe(testRoutes[0].Page);
    });

    it("matches a literal route", () => {
      const result = resolve("/daily", testEntries);
      expect(result?.Page).toBe(testRoutes[1].Page);
    });

    it("captures params", () => {
      const result = resolve("/openlobby/abc/game", testEntries);
      expect(result?.params).toEqual({ lobbyId: "abc" });
    });

    it("follows redirects", () => {
      const result = resolve("/daily/abc", testEntries);
      expect(result?.Page).toBe(testRoutes[1].Page);
    });

    it("returns null for unknown paths without a catch-all", () => {
      const noCatchAll = testRoutes.filter((route) => route.path !== "*");
      expect(resolve("/nope", noCatchAll.map(toEntry))).toBeNull();
    });

    it("falls back to the catch-all", () => {
      const result = resolve("/nope", testEntries);
      expect(result?.Page).toBe(testRoutes[5].Page);
    });
  });
}
